import Fuse, { IFuseOptions } from "fuse.js";
import { NodeDefinition } from "@/generated";
import type { IntegrationInfo } from "@/generated";
import { isSubflowCardNode } from "@/components/SpotflowV2/Sidebar/invokeSubflowBindings";

/**
 * The preferred subflow node type to use when creating new subflow nodes.
 * InvokeSubflowNode (V2) executes subflows in an isolated context.
 */
export const PREFERRED_SUBFLOW_NODE_TYPE = "InvokeSubflowNode";

/**
 * Check if a node type is a subflow node type (either old SubflowNode or new InvokeSubflowNode).
 * This is used for backwards compatibility - existing workflows may have SubflowNode,
 * but new nodes should use InvokeSubflowNode.
 */
export const isSubflowNodeType = (type: string | undefined): boolean => {
  return type === "SubflowNode" || type === "InvokeSubflowNode";
};

/**
 * Check if a node type is an "agent subflow" template — the node that the
 * Agents sidebar menu transforms into a concrete-agent entry by binding an
 * assistant_id. These templates should never appear directly in the search
 * or category menus; users add them via the Agents menu.
 */
export const isAgentSubflowNodeType = (type: string | undefined): boolean => {
  return (
    type === "AgentSubflowNode" ||
    type === "AgentSubflowNodeV2" ||
    type === "AgentSubflowV2Node"
  );
};

/**
 * The generic node that backs every installed plugin node. Its identity lives
 * in hidden {plugin_id, node_name} config; the picker's Plugins category adds
 * it, and its config endpoint resolves its shape from the plugin manifest.
 */
export { isPluginNodeType } from "@/utils/pluginNodeDisplay";

/**
 * Nodes whose name is derived from the bound entity (a saved workflow for the
 * subflow card, or a saved agent for the agent-subflow node) rather than being
 * user-set. They must not be renamable — neither inline on the canvas, via the
 * context-menu "Rename" action / shortcut, nor through the config panel header.
 */
export const isNonRenamableNodeType = (type: string | undefined): boolean => {
  return (
    isSubflowNodeType(type) ||
    isSubflowCardNode(type) ||
    isAgentSubflowNodeType(type)
  );
};

const CATEGORY_PRIORITY = [
  "core",
  "io",
  "flow",
  "logic",
  "data_ops",
  "ai_text",
  "ai",
  "data",
  "ai_image",
  "ai_sound",
  "knowledge",
  "tables",
  "web",
  "files",
  "code",
  "case_management",
  "integrations",
  "agents",
  "subflows",
  "plugins",
];

/**
 * Resolve a node's effective category-priority for search-result ordering.
 * Subflow nodes (SubflowNode / InvokeSubflowNode) are nominally in the
 * Logic category but should rank below case-management and above
 * integrations — so we slot them at case_management + 0.5 instead of
 * inheriting the Logic priority.
 */
const getCategoryPriority = (node: NodeDefinition): number => {
  if (isSubflowNodeType(node.type)) {
    return CATEGORY_PRIORITY.indexOf("case_management") + 0.5;
  }
  const idx = CATEGORY_PRIORITY.indexOf(node.category || "");
  return idx === -1 ? 999 : idx;
};

const FUSE_OPTIONS: IFuseOptions<NodeDefinition> = {
  keys: [
    { name: "title", weight: 3 },
    { name: "type", weight: 2 },
    { name: "tags", weight: 1.5 },
    { name: "description", weight: 1 },
    { name: "sub_category", weight: 0.5 },
  ],
  threshold: 0.4,
  ignoreLocation: false,
  location: 0,
  distance: 50,
  minMatchCharLength: 1,
  includeScore: true,
  shouldSort: true,
  findAllMatches: true,
};

type FilterNodesOptions = {
  nodes: NodeDefinition[];
  searchTerm?: string;
  showInputNode?: boolean;
  workflowType?: string;
  excludePaywalledAndUnavailable?: boolean;
  includeSubflows?: boolean;
  existingNodeTypes?: Set<string>;
  excludeCategories?: string[];
  includeV2Nodes?: boolean;
};

export const filterNodes = ({
  nodes,
  searchTerm = "",
  showInputNode = true,
  workflowType,
  excludePaywalledAndUnavailable = false,
  includeSubflows = false,
  existingNodeTypes,
  excludeCategories = [],
  includeV2Nodes = false,
}: FilterNodesOptions): NodeDefinition[] => {
  let filteredNodes = nodes.filter((node) => {
    if (!node.visible) return false;

    // V2 nodes have no V1 connectors (inputs: [], outputs as NodeOutputV2)
    // and crash the V1 editor — only the V2 pickers may opt in to them.
    if (!includeV2Nodes && node.v2_ready === true) return false;

    if (node.unique && existingNodeTypes?.has(node.type)) {
      return false;
    }

    // Filter by flow type if workflowType is provided. A missing
    // allowed_in_flow_types means the node is allowed in any flow type.
    if (
      workflowType &&
      node.allowed_in_flow_types &&
      !node.allowed_in_flow_types.includes(workflowType)
    ) {
      return false;
    }

    return true;
  });

  // If includeSubflows is true, we should include SubflowNodes/InvokeSubflowNodes even if they're not visible
  if (includeSubflows) {
    const invisibleSubflows = nodes.filter(
      (n) => isSubflowNodeType(n.type) && !n.visible
    );
    filteredNodes = [...filteredNodes, ...invisibleSubflows];
  }

  const isTaskAutomation = workflowType === "task_automation";

  filteredNodes = filteredNodes.filter(
    (node) =>
      node.category !== "admin" &&
      !excludeCategories.includes(node.category ?? "") &&
      (includeSubflows || !isSubflowNodeType(node.type)) &&
      node.type !== "ErrorHandler" &&
      (showInputNode || node.type !== "InputNode") &&
      (!isTaskAutomation ||
        (node.type !== "InputNode" && node.type !== "OutputNode"))
  );

  if (excludePaywalledAndUnavailable) {
    filteredNodes = filteredNodes.filter(
      (node) => node.is_available && !node.paywalled
    );
  }

  const hasSearchTerm = searchTerm.trim().length > 0;

  if (hasSearchTerm) {
    const fuse = new Fuse(filteredNodes, FUSE_OPTIONS);
    const results = fuse.search(searchTerm.trim());
    const searchLower = searchTerm.trim().toLowerCase();

    const sorted = results
      .map((result) => result.item)
      .sort((a, b) => {
        const aTitle = (a.title || "").toLowerCase();
        const bTitle = (b.title || "").toLowerCase();

        const aCategoryPriority = getCategoryPriority(a);
        const bCategoryPriority = getCategoryPriority(b);

        const aTitleStartsWith = aTitle.startsWith(searchLower);
        const bTitleStartsWith = bTitle.startsWith(searchLower);

        if (aTitleStartsWith && !bTitleStartsWith) return -1;
        if (!aTitleStartsWith && bTitleStartsWith) return 1;

        if (aTitleStartsWith && bTitleStartsWith) {
          if (aCategoryPriority !== bCategoryPriority) {
            return aCategoryPriority - bCategoryPriority;
          }
        }

        const aTitleWords = aTitle.split(/\s+/);
        const bTitleWords = bTitle.split(/\s+/);

        const aWordStartsWith = aTitleWords.some((word) =>
          word.startsWith(searchLower)
        );
        const bWordStartsWith = bTitleWords.some((word) =>
          word.startsWith(searchLower)
        );

        if (aWordStartsWith && !bWordStartsWith) return -1;
        if (!aWordStartsWith && bWordStartsWith) return 1;

        if (aWordStartsWith && bWordStartsWith) {
          if (aCategoryPriority !== bCategoryPriority) {
            return aCategoryPriority - bCategoryPriority;
          }
        }

        if (aCategoryPriority !== bCategoryPriority) {
          return aCategoryPriority - bCategoryPriority;
        }

        return 0;
      });

    return sorted;
  }

  const finalSorted = filteredNodes.sort((a, b) => {
    if (!a?.title || !b?.title) return 0;

    if (a?.is_available && !b?.is_available) {
      return -1;
    } else if (!a?.is_available && b?.is_available) {
      return 1;
    }

    const priorityDiff = (b.picker_priority ?? 0) - (a.picker_priority ?? 0);
    if (priorityDiff !== 0) return priorityDiff;

    return a.title.localeCompare(b.title);
  });

  return finalSorted;
};

export const getSortedNodes = (nodes: NodeDefinition[]): NodeDefinition[] => {
  return filterNodes({ nodes });
};

export const getFilteredNodesForFlow = (
  nodes: NodeDefinition[],
  showInputNode?: boolean,
  workflowType?: string
): NodeDefinition[] => {
  return filterNodes({ nodes, showInputNode, workflowType });
};

export const searchNodes = (
  nodes: NodeDefinition[],
  searchTerm: string
): NodeDefinition[] => {
  return filterNodes({ nodes, searchTerm });
};

/**
 * Build a set of node names that are disabled by admin (platform-level)
 * from the integration info response.
 */
export function buildAdminDisabledNodes(
  integrations: IntegrationInfo[]
): Set<string> {
  const disabled = new Set<string>();
  for (const integration of integrations) {
    for (const node of integration.nodes) {
      if (!node.admin_enabled) {
        disabled.add(node.node_name);
      }
    }
  }
  return disabled;
}
